import { RichText, RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';

import styles from './Table.module.scss';
import type { TableCellData, TableColumnData, TableRowData, TableStyles } from './Table.types';

/** Properties of the Table component. */
export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Caption of the table element. */
  caption?: string;

  /** Custom classname of the table element. */
  className?: string;

  /**
   * Properties used to generate table columns.
   * @defaultValue `[]`
   */
  columnData?: TableColumnData[];

  /**
   * Unique identifier of the table.
   * The `id` must be unique (per page) for accessibility purposes.
   */
  id: string;

  /**
   * Properties used to generate table rows and cells.
   * @defaultValue `[]`
   */
  rowData?: TableRowData[];

  /** Table styling options. */
  styles?: TableStyles;
}

/** Default properties of the Table component. */
const defaultProps: Partial<TableProps> = {
  columnData: [],
  rowData: [],
};

/**
 * Classnames binding for the Table component.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A Sitecore-ready implementation of the `<Table>` component.
 *
 * @see [Orchid Design System](https://upmchporchid.azurewebsites.net/components/table) for usage details

 * @param props - Table properties.
 * @returns A JSX element.
 */
const Table: React.FC<TableProps> = (props) => {
  const { caption, className, columnData = [], styles: tableStyles = {}, id, rowData = [] } = props;

  const classes = cx('table', className);
  const tableId = `table-${id}`;

  const columnElements = columnData?.map((columnDataItem: TableColumnData, columnIndex: number) => {
    const { className: columnClassName, span = 1 } = columnDataItem;

    const columnClasses = cx('table__column', columnClassName);
    const columnKey = `${tableId}__column-${columnIndex}`;

    return <col className={columnClasses} key={columnKey} span={span} />;
  });

  const headerRowElements: React.ReactNode[] = [];
  const footerRowElements: React.ReactNode[] = [];
  const bodyRowElements: React.ReactNode[] = [];

  rowData?.forEach((rowDataItem: TableRowData, rowIndex: number) => {
    const { cellData, className: rowClassName, styles: rowStyles, placement } = rowDataItem;

    const rowClasses = cx('table__row', rowClassName);
    const rowKey = `${tableId}__row-${rowIndex}`;

    const cellElements = cellData?.map((cellDataItem: TableCellData, cellIndex: number) => {
      const {
        children,
        className: cellClassName,
        colSpan,
        styles: cellStyles,
        headers,
        id: cellId,
        rowSpan,
        scope,
      } = cellDataItem;

      const columnDataItem = columnData[cellIndex];
      const columnStyles = columnDataItem?.styles;
      const isHeader = placement === 'header' || cellDataItem?.isHeader;

      const combinedStyles = {
        ...tableStyles,
        ...columnStyles,
        ...rowStyles,
        ...cellStyles,
      };

      const cellClasses = cx(
        'table__cell',
        {
          [`table__cell--align-${combinedStyles?.align}`]: combinedStyles?.align,
          [`table__cell--border-${combinedStyles?.border}`]: combinedStyles?.border,
          [`table__cell--color-${combinedStyles?.color}`]: combinedStyles?.color,
          [`table__cell--header`]: isHeader,
        },
        cellClassName
      );

      const cellKey = `${rowKey}__cell-${cellIndex}` || cellId;
      const sharedProps = { className: cellClasses, colSpan, headers, id: cellId, key: cellKey, rowSpan };

      return isHeader ? (
        <th {...sharedProps} scope={scope}>
          {children}
        </th>
      ) : (
        <td {...sharedProps}>{children}</td>
      );
    });

    const rowElement = (
      <tr className={rowClasses} key={rowKey}>
        {cellElements}
      </tr>
    );

    if (placement === 'header') {
      headerRowElements.push(rowElement);
    } else if (placement === 'footer') {
      footerRowElements.push(rowElement);
    } else {
      bodyRowElements.push(rowElement);
    }
  });

  return (
    <table className={classes} id={tableId}>
      {caption ? (
        <caption className={cx('table__caption')}>
          <RichText field={{ value: caption } as RichTextField} />
        </caption>
      ) : (
        ''
      )}
      {columnElements?.length > 0 ? <colgroup className={cx('table__colgroup')}>{columnElements}</colgroup> : ''}
      {headerRowElements?.length > 0 ? <thead className={cx('table__header')}>{headerRowElements}</thead> : ''}
      <tbody className={cx('table__body')}>{bodyRowElements}</tbody>
      {footerRowElements?.length > 0 ? <tfoot className={cx('table__footer')}>{footerRowElements}</tfoot> : ''}
    </table>
  );
};

Table.defaultProps = defaultProps;
Table.displayName = 'Table';

export default Table;
